import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';

const PAGE_MARGIN_X = 54;
const PAGE_MARGIN_TOP = 56;
const PAGE_MARGIN_BOTTOM = 56;
const PAGE_WIDTH = 612;
const PAGE_HEIGHT = 792;
const BODY_WIDTH = PAGE_WIDTH - PAGE_MARGIN_X * 2;
const BODY_FONT_SIZE = 11;
const BODY_LINE_HEIGHT = 15;

const normalizeBody = (value: string): string[] =>
  String(value ?? '')
    .replace(/\r\n/g, '\n')
    .replace(/\uFEFF/g, '')
    .split('\n')
    .map((entry) => entry.replace(/\s+$/g, ''));

const wrapLine = (
  text: string,
  maxWidth: number,
  measure: (value: string) => number
): string[] => {
  const tokens = text.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return [''];
  const lines: string[] = [];
  let current = '';

  for (const token of tokens) {
    const proposal = current ? `${current} ${token}` : token;
    if (measure(proposal) <= maxWidth) {
      current = proposal;
      continue;
    }
    if (current) {
      lines.push(current);
      current = token;
      continue;
    }
    lines.push(token);
  }

  if (current) {
    lines.push(current);
  }
  return lines;
};

const readPngDataUrl = (value: string | null | undefined): Uint8Array | null => {
  const match = String(value ?? '').match(/^data:image\/png;base64,(.+)$/);
  if (!match?.[1] || typeof globalThis.atob !== 'function') return null;
  try {
    const binary = globalThis.atob(match[1]);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    return bytes;
  } catch {
    return null;
  }
};

export const buildTuitionContractPdf = async (input: {
  title?: string;
  body: string;
  signerName?: string | null;
  signatureDataUrl?: string | null;
  signedAtLabel?: string | null;
}): Promise<Uint8Array> => {
  const pdf = await PDFDocument.create();
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);

  const lines = normalizeBody(input.body);
  let page = pdf.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  let cursorY = PAGE_HEIGHT - PAGE_MARGIN_TOP;

  const addPage = () => {
    page = pdf.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
    cursorY = PAGE_HEIGHT - PAGE_MARGIN_TOP;
  };

  if (input.title) {
    page.drawText(input.title, {
      x: PAGE_MARGIN_X,
      y: cursorY,
      size: 14,
      font: bold,
      color: rgb(0.08, 0.09, 0.11),
    });
    cursorY -= 24;
  }

  for (const rawLine of lines) {
    const line = rawLine.trimEnd();
    const measuredLines = line
      ? wrapLine(line, BODY_WIDTH, (value) => font.widthOfTextAtSize(value, BODY_FONT_SIZE))
      : [''];

    for (const wrappedLine of measuredLines) {
      if (cursorY < PAGE_MARGIN_BOTTOM) {
        addPage();
      }

      if (wrappedLine) {
        page.drawText(wrappedLine, {
          x: PAGE_MARGIN_X,
          y: cursorY,
          size: BODY_FONT_SIZE,
          font,
          color: rgb(0.08, 0.09, 0.11),
        });
      }
      cursorY -= BODY_LINE_HEIGHT;
    }

    if (!line) {
      cursorY -= 4;
    }
  }

  const signerName = String(input.signerName ?? '').trim();
  const signatureBytes = readPngDataUrl(input.signatureDataUrl);
  const signedAtLabel = String(input.signedAtLabel ?? '').trim();

  if (signerName || signatureBytes || signedAtLabel) {
    if (cursorY < PAGE_MARGIN_BOTTOM + 118) {
      addPage();
    }

    page.drawText('Parent/Guardian Signature', {
      x: PAGE_MARGIN_X,
      y: cursorY,
      size: 12,
      font: bold,
      color: rgb(0.08, 0.09, 0.11),
    });
    cursorY -= 18;

    if (signerName) {
      page.drawText(`Signed by: ${signerName}`, {
        x: PAGE_MARGIN_X,
        y: cursorY,
        size: BODY_FONT_SIZE,
        font,
        color: rgb(0.08, 0.09, 0.11),
      });
      cursorY -= BODY_LINE_HEIGHT;
    }

    if (signedAtLabel) {
      page.drawText(`Signed on: ${signedAtLabel}`, {
        x: PAGE_MARGIN_X,
        y: cursorY,
        size: BODY_FONT_SIZE,
        font,
        color: rgb(0.08, 0.09, 0.11),
      });
      cursorY -= BODY_LINE_HEIGHT;
    }

    if (signatureBytes) {
      try {
        const signatureImage = await pdf.embedPng(signatureBytes);
        const scale = Math.min(240 / signatureImage.width, 72 / signatureImage.height, 1);
        const dimensions = signatureImage.scale(scale);
        if (cursorY - dimensions.height < PAGE_MARGIN_BOTTOM) addPage();
        page.drawImage(signatureImage, {
          x: PAGE_MARGIN_X,
          y: cursorY - dimensions.height,
          width: dimensions.width,
          height: dimensions.height,
        });
      } catch {
        page.drawText('Signature captured electronically.', {
          x: PAGE_MARGIN_X,
          y: cursorY,
          size: BODY_FONT_SIZE,
          font,
          color: rgb(0.08, 0.09, 0.11),
        });
      }
    }
  }

  return pdf.save();
};

export const appendTuitionContractAcceptancePage = async (input: {
  originalPdfBytes: Uint8Array;
  signerName: string;
  signatureDataUrl?: string | null;
  signedAtLabel: string;
  paymentPreferenceLabel: string;
  paymentAmountLabel: string;
  installmentSummaryLabel?: string | null;
  sourceVersion?: string | null;
}): Promise<Uint8Array> => {
  const pdf = await PDFDocument.load(input.originalPdfBytes);
  const regular = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
  const page = pdf.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  let cursorY = PAGE_HEIGHT - PAGE_MARGIN_TOP;

  const drawText = (
    value: string,
    options: { size?: number; font?: typeof regular; yGap?: number } = {},
  ) => {
    const size = options.size ?? BODY_FONT_SIZE;
    page.drawText(value, {
      x: PAGE_MARGIN_X,
      y: cursorY,
      size,
      font: options.font ?? regular,
      color: rgb(0.08, 0.09, 0.11),
    });
    cursorY -= options.yGap ?? size + 8;
  };

  drawText('Tuition Contract Acceptance', { size: 18, font: bold, yGap: 30 });
  drawText(`Signed by: ${input.signerName}`, { font: bold });
  drawText(`Signed on: ${input.signedAtLabel}`);
  drawText(`Payment preference: ${input.paymentPreferenceLabel}`);
  drawText(`Payment amount: ${input.paymentAmountLabel}`);
  if (input.installmentSummaryLabel) {
    drawText(input.installmentSummaryLabel);
  }
  if (input.sourceVersion) {
    drawText(`Contract version: ${input.sourceVersion}`, { size: 9, yGap: 24 });
  } else {
    cursorY -= 12;
  }

  page.drawText('Parent/Guardian Signature', {
    x: PAGE_MARGIN_X,
    y: cursorY,
    size: 12,
    font: bold,
    color: rgb(0.08, 0.09, 0.11),
  });
  cursorY -= 18;

  const signatureBytes = readPngDataUrl(input.signatureDataUrl);
  if (signatureBytes) {
    try {
      const signatureImage = await pdf.embedPng(signatureBytes);
      const scale = Math.min(320 / signatureImage.width, 96 / signatureImage.height, 1);
      const dimensions = signatureImage.scale(scale);
      page.drawImage(signatureImage, {
        x: PAGE_MARGIN_X,
        y: cursorY - dimensions.height,
        width: dimensions.width,
        height: dimensions.height,
      });
      cursorY -= dimensions.height + 24;
    } catch {
      drawText('Signature captured electronically.');
    }
  } else {
    drawText('Signature captured electronically.');
  }

  page.drawLine({
    start: { x: PAGE_MARGIN_X, y: cursorY },
    end: { x: PAGE_WIDTH - PAGE_MARGIN_X, y: cursorY },
    thickness: 0.8,
    color: rgb(0.4, 0.45, 0.47),
  });
  page.drawText('This page records the family acceptance of the attached tuition contract.', {
    x: PAGE_MARGIN_X,
    y: PAGE_MARGIN_BOTTOM,
    size: 9,
    font: regular,
    color: rgb(0.4, 0.45, 0.47),
  });

  return pdf.save();
};
